import {
  Backdrop,
  Button,
  Divider,
  Fade,
  Grid,
  IconButton,
  Modal,
  Typography,
  makeStyles,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import DeleteForeverIcon from "@material-ui/icons/DeleteForever";
import React, { useEffect, useState } from "react";
import { getUserContactStageProducts, getUserPolicies, updateSuccessDealPolicy } from "../../api/dealApi";
import { getInsuranceCompanies } from "../../api/tenantApi";
import Utils from "../../helpers/Utils";
import {
  BasicAddButton,
  BasicFormLabel,
  BasicInput,
  ContentWrapper
} from "../GlobalAddDealModalV3/GlobalAddDealModalStyles";
import CustomTitleAddDeal from "../GlobalAddDealModalV3/components/CustomTitleAddDeal";
import BasicSelectWithSearch from "../common/BasicSelectWithSearch";
import Loading from "./Loading";
import TrophyAlert from "./NewAlert/TrophyAlert";

const modalStyles = makeStyles((theme) => ({
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  paper: {
    width: 1230,
    backgroundColor: theme.palette.background.paper,
    borderRadius: "8px",
    boxShadow: theme.shadows[5],
    "&:focus-visible": {
      outline: "none !important",
    },
  },
  modalHeader: {
    padding: "8px 16px",
    background: "#f6f7fc",
    color: "#000000",
    borderTopRightRadius: "8px",
    borderTopLeftRadius: "8px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",

    "& .closeIcon": {
      display: "flex",
      cursor: "pointer",
      "& svg": {
        color: "#fff",
        backgroundColor: "var(--red)",
        width: "25px",
        height: "25px",
        borderRadius: "50px",
      },
    },
  },
  modalBody: {
    padding: "16px",
    minHeight: 400,
    "& .productValueSignDisabled": {
      color: "rgba(0,0,0,0.42)",
    },
  },
  modalBodyInput: {
    display: "flex",
    alignItems: "center",
    gap: "20px",
  },

  modalFooter: {
    background: "#f6f7fc",
    borderEndEndRadius: "8px",
    borderEndStartRadius: "8px",
    padding: "16px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",

    "& .title": {
      color: "#fff",
      fontWeight: "600",
    },

    "& .btnSubmit": {
      background: "#3e50f7 !important",
      fontWeight: "600",
      padding: "6px 32px",
    },
    "& .btnSkip": {
      background: "#ffffff !important",
      fontWeight: "600",
      padding: "6px 32px",
    },
    "& .cancel": {
      background: "#ff0055 !important",
      fontWeight: "600",
    },
  },
  errorText: {
    color: "red",
    marginTop: "4px",
    fontSize: "10px",
  },
  divider: {
    flex: 1,
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    marginBottom: "0px !important",
    paddingBottom: "0px !important",
  },
  logoContainer: {
    width: "55px",
    height: "55px",
  },
  logo: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
}));

const formatDateForInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const ConfirmDealModal = ({ open, onClose, contact_stages_id, data, handleUpdateDealStatus }) => {
  const classes = modalStyles();
  const fileImage = `${process.env.REACT_APP_CDN_LINK}assets/images/releases/1.0.0/good-rating_5939775.png`;
  const today = new Date();
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formattedDate = formatDateForInput(today);
  const [title, setTitle] = useState("");
  const [dealValue, setDealValue] = useState(0);
  const [dealCloseDate, setDealCloseDate] = useState(formattedDate);
  const [userPolicies, setUserPolicies] = useState([]);
  const [userPolicyCompanies, setUserPolicyCompanies] = useState([]);
  const [contactStagePolicies, setContactStagePolicies] = useState([]);
  const [errors, setErrors] = useState({
    title: "",
    deal_closed_date: "",
    deal_value: "",
    user_policy_id: "",
    insurance_company_id: "",
    userPolicies: [],
  });

  useEffect(() => {
    getData()
  }, []);

  useEffect(() => {
    if (contactStagePolicies[0]) {
      let productSum = contactStagePolicies
        .reduce((accumulator, currentObject) => (accumulator + (parseFloat(currentObject.value) || 0)), 0);
      setDealValue(productSum)
    }
  }, [contactStagePolicies]);

  const getData = async () => {
    try {
      setIsLoading(true);
      const [
        resp1,
        resp2,
        resp3
      ] =
        await Promise.all([
          getInsuranceCompanies(),
          getUserPolicies(),
          getUserContactStageProducts(contact_stages_id)
        ]);

      if (resp1 && resp1.success) {
        setUserPolicyCompanies(resp1.data);
      } else {
        setUserPolicyCompanies([]);
      }

      if (resp2 && resp2.success) {
        setUserPolicies(resp2.data);
      }

      if (resp3.success) {
        setTitle(resp3.data.contactStage?.title);
        const closeDate = resp3.data.contactStage?.estimate_closing_date?.split(" ")[0];
        setDealCloseDate(closeDate || formattedDate);
        setDealValue(resp3.data.contactStage?.deal_value);

        let mappedPolicies = resp3.data.policies.map((policy)=>({ ...policy, effective_date: policy?.effective_date ?? formattedDate, expiration_date: policy?.expiration_date ?? formattedDate }));

        setContactStagePolicies(mappedPolicies);
      }
    } catch (err) {

    } finally {
      setIsLoading(false);
    }
  }

  const handleAddPolicy = () => {
    let old = [...contactStagePolicies];

    old.push({
      user_policy_id: "",
      insurance_company_id: "",
      value: 0,
      effective_date: formattedDate,
      expiration_date: formattedDate,
      policy_number: "",
      item_count: 1,
    });

    setContactStagePolicies(old);

    setErrors((prevErrors) => ({
      ...prevErrors,
      userPolicies: [
        ...prevErrors.userPolicies,
        {
          user_policy_id: "",
          insurance_company_id: "",
          value: 0,
          effective_date: "",
          expiration_date: "",
          policy_number: "",
          item_count: ""
        }
      ],
    }));
  };

  const handleAnnualizedPremiumChange = (index, value) => {
    let old = [...contactStagePolicies];
    let errorCopy = [...errors.userPolicies];

    if (typeof value === "string" && value.trim() !== "") {
      if (value.length > 10) {
        return;
      }

      const regex = /^[0-9]*\.?[0-9]*$/;

      if (!regex.test(value)) {
        return;
      }

      if (value === ".") {
        value = "0.";
      }

      let parsedDealValue = dealValue;
      const selectedValue = value;

      if (old[index]["value"] === 0) {
        if (parsedDealValue + selectedValue >= 0) {
          if (old?.length === 1) {
            parsedDealValue = 0;
          }
          let temDealValue = parseFloat(parsedDealValue + selectedValue);
          setDealValue(temDealValue);
        } else {
          setDealValue(0);
        }
      } else if (old[index]["value"] >= selectedValue) {
        if (parsedDealValue - parseFloat(old[index]["value"]) + selectedValue >= 0) {
          let temDealValue = parseFloat(parsedDealValue + old[index]["value"] + selectedValue);
          setDealValue(temDealValue);
        } else {
          setDealValue(0);
        }
      } else {
        if (!old[index]["value"]) {
          if (parsedDealValue + selectedValue >= 0) {
            let temDealValue = parseFloat(parsedDealValue + selectedValue);
            setDealValue(temDealValue);
          } else {
            setDealValue(0);
          }
        } else {
          if (parsedDealValue + selectedValue - parseFloat(old[index]["value"]) >= 0) {
            let temDealValue = parseFloat(parsedDealValue + selectedValue) - old[index]["value"];
            setDealValue(temDealValue);
          } else {
            setDealValue(0);
          }
        }
      }
    }

    old[index]["value"] = value;
    setContactStagePolicies(old);
    setErrors((prevErrors) => ({ ...prevErrors, userPolicies: errorCopy }));
  };

  const handlePolicyNumberChange = (index, value) => {
    let old = [...contactStagePolicies];
    let errorCopy = [...errors.userPolicies];

    if (typeof value === "string" && value.trim() !== "") {
      if (value.length > 100) {
        return;
      }
    }

    old[index]["policy_number"] = value;

    if (value === "") {
      if (errorCopy[index]) {
        errorCopy[index].policy_number = "Policy number cannot be empty";
      }
    } else {
      if (errorCopy[index]) {
        errorCopy[index].policy_number = "";
      }
    }

    setContactStagePolicies(old);
    setErrors((prevErrors) => ({ ...prevErrors, userPolicies: errorCopy }));
  };

    const handlePolicyItemsChange = (index, value) => {
        let old = [...contactStagePolicies];
        let errorCopy = [...errors.userPolicies];

        if (typeof value === "string" && value.trim() !== "") {
            if (value.length > 100) {
                return;
            }
        }

        old[index]["item_count"] = value;

        if (value === "") {
            if (errorCopy[index]) {
                errorCopy[index].item_count = "Items cannot be empty";
            }
        } else {
            if (errorCopy[index]) {
                errorCopy[index].item_count = "";
            }
        }

        setContactStagePolicies(old);
        setErrors((prevErrors) => ({ ...prevErrors, userPolicies: errorCopy }));
    };

  const handleUserPolicyIdChange = (index, value) => {
    let old = [...contactStagePolicies];
    let errorCopy = [...errors.userPolicies];

    old[index]["user_policy_id"] = value;

    if (value === "") {
      if (errorCopy[index]) {
        errorCopy[index].user_policy_id = "Policy cannot be empty";
      }
    } else {
      if (errorCopy[index]) {
        errorCopy[index].user_policy_id = "";
      }
    }

    setContactStagePolicies(old);
    setErrors((prevErrors) => ({ ...prevErrors, userPolicies: errorCopy }));
  };

  const handleInsuranceCompanyIdChange = (index, value) => {
    let old = [...contactStagePolicies];
    let errorCopy = [...errors.userPolicies];

    old[index]["insurance_company_id"] = value;

    if (value === "") {
      if (errorCopy[index]) {
        errorCopy[index].insurance_company_id = "Insurance company cannot be empty";
      }
    } else {
      if (errorCopy[index]) {
        errorCopy[index].insurance_company_id = "";
      }
    }

    setContactStagePolicies(old);
    setErrors((prevErrors) => ({ ...prevErrors, userPolicies: errorCopy }));
  };

  const handleEffectiveDateChange = (index, value) => {
    let old = [...contactStagePolicies];
    let errorCopy = [...errors.userPolicies];

    old[index]["effective_date"] = value;

    if (value === "") {
      if (errorCopy[index]) {
        errorCopy[index].effective_date = "Effective date cannot be empty";
      }
    } else {
      if (errorCopy[index]) {
        errorCopy[index].effective_date = "";
      }
    }

    setContactStagePolicies(old);
    setErrors((prevErrors) => ({ ...prevErrors, userPolicies: errorCopy }));
  };

  const handleExpirationDateChange = (index, value) => {
    let old = [...contactStagePolicies];
    let errorCopy = [...errors.userPolicies];

    old[index]["expiration_date"] = value;

    if (value === "") {
      if (errorCopy[index]) {
        errorCopy[index].expiration_date = "Expiration date cannot be empty";
      }
    } else {
      if (errorCopy[index]) {
        errorCopy[index].expiration_date = "";
      }
    }

    setContactStagePolicies(old);
    setErrors((prevErrors) => ({ ...prevErrors, userPolicies: errorCopy }));
  };

  const handleDeletePolicy = (index) => {
    let old = [...contactStagePolicies];
    let errorCopy = [...errors.userPolicies];

    old.splice(index, 1);
    errorCopy.splice(index, 1);

    setContactStagePolicies(old);
    setErrors((prevErrors) => ({ ...prevErrors, userPolicies: errorCopy }));
  };

  const validateForm = () => {
    let formValid = true;
    let errors = { title: "", deal_value: "", userPolicies: [] };

    contactStagePolicies.forEach((product, index) => {
      if (product?.user_policy_id === "") {
        errors.userPolicies[index] = { user_policy_id: "Policy cannot be empty" };
        formValid = false;
      }

      if (product?.policy_number === "" || product?.policy_number === null || product?.policy_number === undefined) {
        errors.userPolicies[index] = { policy_number: "Policy number cannot be empty" };
        formValid = false;
      }
    });

    setErrors(errors);
    return formValid;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    const payload = {
      deal_title: title,
      deal_closed_date: window.globalTimezoneConversionToDifferentTimezone(dealCloseDate, Utils.getAccountData("userTimezoneRaw"), 'UTC', 'YYYY-MM-DD HH:mm:ss'),
      deal_value: dealValue,
      user_policies: contactStagePolicies,
      contact_stage_id: contact_stages_id
    };

    try {
      setIsSubmitting(true);
      const reps = await updateSuccessDealPolicy(payload);
      if (reps.success) {
        data?.callback({ success: true });
        window.showNotification("SUCCESS", reps.message);
        onClose();
      } else {
        window.showNotification("ERROR", reps.message);
        onClose();
      }
    } catch (err) {
      console.log(err)
    } finally {
      setIsSubmitting(false);
       TrophyAlert({
         title: "Well Done " + Utils.getAccountData("fullName") + "!",
         // setConfirmDealModal: setConfirmDealModal,
         closeOnClickOutside: true,
       });
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => {}}
      className={classes.modal}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
    >
      <Fade in={open}>
        <div className={classes.paper}>
          <div className={classes.modalHeader}>
            <div className={classes.logoContainer}>
              <img src={fileImage} alt="logo" className={classes.logo} />
            </div>
            <Typography
              style={{
                height: "unset !important",
                color: "black",
                fontWeight: 600,
              }}
              variant="h5"
            >
              Confirm Your Deal Information
            </Typography>
            <div className={classes.divider}>
              <Divider width="100%" />
            </div>
            <Typography
              style={{
                height: "unset !important",
                color: "black",
                fontWeight: 600,
              }}
              variant="h5"
            >
              Deal Value: ${Utils.formatCurrency(dealValue)}
            </Typography>
          </div>

          <div className={classes.modalBody}>
            {isLoading ? (
              <Loading />
            ) : (
              <ContentWrapper>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography
                      style={{
                        height: "unset !important",
                        fontWeight: 600,
                        color: "#133159",
                      }}
                      className={errors.title ? classes.errorText : ""}
                    >
                      Deal Title
                    </Typography>
                    <div>
                      <BasicInput
                        fullWidth
                        value={title}
                        onChange={(e) => {
                          setTitle(e.target.value);
                          validateForm();
                        }}
                        required
                        className={errors.title ? classes.errorStyle : ""}
                        placeholder="Enter Deal Title"
                      />
                      {errors.title && (
                        <Typography
                          style={{ height: "unset !important" }}
                          variant="body2"
                          className={classes.errorText}
                        >
                          {errors.title}
                        </Typography>
                      )}
                    </div>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography
                      style={{
                        height: "unset !important",
                        fontWeight: 600,
                        color: "#133159",
                      }}
                    >
                      Close Date
                    </Typography>
                    <div>
                      <BasicInput
                        type="date"
                        fullWidth
                        value={dealCloseDate}
                        onChange={(e) => {
                          setDealCloseDate(e.target.target);
                        }}
                      />
                      {errors.deal_closed_date && (
                        <Typography
                          style={{ height: "unset !important" }}
                          variant="body2"
                          className={classes.errorText}
                        >
                          {errors.deal_closed_date}
                        </Typography>
                      )}
                    </div>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography
                      style={{
                        height: "unset !important",
                        fontWeight: 600,
                        color: "#133159",
                      }}
                    >
                      Deal Value
                    </Typography>
                    <div className="productValueInputWrapper">
                      <span
                        className={`productValueSign ${
                          dealValue ? "" : "productValueSignDisabled"
                        }`}
                      >
                        $
                      </span>
                      <BasicInput
                        name="dealValue"
                        value={dealValue}
                        fullWidth
                        onChange={(e) => {
                          setDealValue(e.target.value);
                        }}
                        placeholder="Enter Deal Value"
                      />
                      {errors.deal_value && (
                        <Typography
                          style={{ height: "unset !important" }}
                          variant="body2"
                          className={classes.errorText}
                        >
                          {errors.deal_value}
                        </Typography>
                      )}
                    </div>
                  </Grid>
                </Grid>

                <Grid
                  style={{
                    maxHeight: "calc(100vh - 400px)",
                    overflowY: "auto",
                    overflowX: "hidden",
                    marginTop: 16,
                  }}
                  container
                  spacing={2}
                >
                  <Grid item xs={12}>
                    <CustomTitleAddDeal
                      title={"Lines of business associated with the deal:"}
                    />
                  </Grid>

                  <Grid
                    container
                    style={{ marginTop: "4px", padding: "0px 8px" }}
                  >
                    {contactStagePolicies?.map((product, index) => (
                      <div
                        className="productItem"
                        key={index}
                        style={{ display: 'flex' }}
                      >
                        <div style={{ width: '210px', marginRight: '10px' }}>
                          <BasicFormLabel variant="body1">
                            Policy Type
                          </BasicFormLabel>
                          <BasicSelectWithSearch
                            placeholderText="Search for policy here..."
                            name="user_policy_id"
                            options={userPolicies}
                            mapping={{
                              label: "title",
                              value: "id",
                            }}
                            deletedMapping={{
                              field: "status",
                              value: "TRASH",
                            }}
                            defaultText="Select policy"
                            value={product.user_policy_id}
                            onChange={(e) => {
                              handleUserPolicyIdChange(index, e.target.value);
                            }}
                            selectedOption={(option) => {
                              handleAnnualizedPremiumChange(
                                index,
                                option.unit_price
                              );
                            }}
                          />
                          {errors.userPolicies[index]?.user_policy_id && (
                            <Typography
                              style={{ height: "unset !important" }}
                              variant="body2"
                              className={classes.errorText}
                            >
                              {errors.userPolicies[index]?.user_policy_id}
                            </Typography>
                          )}
                        </div>

                        <div style={{ width: '210px', marginRight: '10px' }}>
                          <BasicFormLabel variant="body1">
                            Insurance Company
                          </BasicFormLabel>
                          <BasicSelectWithSearch
                            placeholderText="Search for company here..."
                            name="insurance_company_id"
                            options={userPolicyCompanies}
                            mapping={{
                              label: "company_name",
                              value: "id",
                            }}
                            deletedMapping={{
                              field: "status",
                              value: "TRASH",
                            }}
                            defaultText="Select company"
                            value={product.insurance_company_id || ""}
                            onChange={(e) => {
                              handleInsuranceCompanyIdChange(
                                index,
                                e.target.value
                              );
                            }}
                          />
                          {errors.userPolicies[index]?.user_policy_id && (
                            <Typography
                              style={{ height: "unset !important" }}
                              variant="body2"
                              className={classes.errorText}
                            >
                              {errors.userPolicies[index]?.user_policy_id}
                            </Typography>
                          )}
                        </div>

                        <div style={{ width: '155px', marginRight: '10px' }}>
                          <BasicFormLabel variant="body1">
                            Annualized Premium
                          </BasicFormLabel>
                          <div className="productValueInputWrapper">
                            <span
                              className={`productValueSign${
                                product.user_policy_id === ""
                                  ? " productValueSignDisabled"
                                  : ""
                              }`}
                            >
                              $
                            </span>
                            <BasicInput
                              name="value"
                              value={product.value}
                              fullWidth
                              disabled={
                                product.user_policy_id === "" ? true : false
                              }
                              onChange={(e) => {
                                handleAnnualizedPremiumChange(
                                  index,
                                  e.target.value
                                );
                              }}
                              placeholder="Enter Annualized Premium"
                            />
                          </div>
                        </div>

                        <div style={{ width: '180px', marginRight: '10px' }}>
                          <BasicFormLabel variant="body1">
                            Policy Number
                          </BasicFormLabel>
                          <BasicInput
                              name="policy_number"
                              value={product.policy_number}
                              fullWidth
                              disabled={
                                product.user_policy_id === "" ? true : false
                              }
                              onChange={(e) => {
                                handlePolicyNumberChange(
                                    index,
                                    e.target.value
                                );
                              }}
                              placeholder="Enter Policy #"
                          />
                          {errors.userPolicies[index]?.policy_number && (
                              <Typography
                                  style={{ height: "unset !important" }}
                                  variant="body2"
                                  className={classes.errorText}
                              >
                                {errors.userPolicies[index]?.policy_number}
                              </Typography>
                          )}
                        </div>

                        <div style={{ width: '50px', marginRight: '10px' }}>
                              <BasicFormLabel variant="body1">
                                  Items
                              </BasicFormLabel>
                              <BasicInput
                                  name="item_count"
                                  value={product.item_count}
                                  fullWidth
                                  onChange={(e) => {
                                      handlePolicyItemsChange(
                                          index,
                                          e.target.value
                                      );
                                  }}
                                  placeholder="Enter Items"
                              />
                              {errors.userPolicies[index]?.item_count && (
                                  <Typography
                                      style={{ height: "unset !important" }}
                                      variant="body2"
                                      className={classes.errorText}
                                  >
                                      {errors.userPolicies[index]?.item_count}
                                  </Typography>
                              )}
                          </div>

                        <div style={{ width: '150px', marginRight: '10px' }}>
                          <BasicFormLabel variant="body1">
                            Effective Date
                          </BasicFormLabel>
                          <div className="productValueWrapper">
                            <BasicInput
                              name="effective_date"
                              type="date"
                              fullWidth
                              value={product.effective_date || formattedDate}
                              onChange={(e) => {
                                handleEffectiveDateChange(
                                  index,
                                  e.target.value
                                );
                              }}
                            />
                          </div>
                        </div>

                        <div style={{ width: '150px', marginRight: '10px' }}>
                          <BasicFormLabel variant="body1">
                            Expiration Date
                          </BasicFormLabel>
                          <div className="productValueWrapper">
                            <BasicInput
                                name="expiration_date"
                                type="date"
                                fullWidth
                                value={product.expiration_date || formattedDate}
                                onChange={(e) => {
                                  handleExpirationDateChange(
                                      index,
                                      e.target.value
                                  );
                                }}
                            />
                            <IconButton
                                size="small"
                                onClick={() => {
                                  handleDeletePolicy(index);
                                }}
                            >
                              <DeleteForeverIcon color={"error"} />
                            </IconButton>
                          </div>
                        </div>
                      </div>
                    ))}
                  </Grid>
                  <Grid item xs={12}>
                    <BasicAddButton
                      startIcon={<AddIcon />}
                      onClick={handleAddPolicy}
                    >
                      Add New Item
                    </BasicAddButton>
                  </Grid>
                </Grid>
              </ContentWrapper>
            )}
          </div>

          <div className={classes.modalFooter}>
            <div>
              <Button
                disabled={isSubmitting}
                className="cancel"
                variant="contained"
                color="primary"
                onClick={() => {
                  onClose();
                }}
              >
                Cancel
              </Button>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Button
                disabled={isSubmitting}
                className="btnSkip"
                variant="outlined"
                color="black"
                onClick={() => {
                  handleUpdateDealStatus();
                  onClose();
                }}
              >
                Skip
              </Button>
              <Button
                disabled={isSubmitting}
                className="btnSubmit #3e50f7"
                variant="contained"
                color="primary"
                onClick={handleSubmit}
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      </Fade>
    </Modal>
  );
};

export default ConfirmDealModal;
